import React from 'react';
import { Instagram, Facebook, Linkedin, Youtube, Twitter, Globe } from 'lucide-react';
import { CONTENT_CHANNELS, getChannelMeta, getPostChannels } from '../lib/contentStates';

const TikTokIcon = ({ className, style }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} style={style} aria-hidden="true">
    <path d="M12.5 2h3.1c.2 1.9 1.3 3.5 3 4.3.9.5 1.9.7 2.9.7v3.1c-1.9 0-3.7-.6-5.2-1.6v7.2c0 3.5-2.9 6.3-6.4 6.3S3.5 19.2 3.5 15.7s2.9-6.3 6.4-6.3c.3 0 .6 0 .9.1v3.2c-.3-.1-.6-.1-.9-.1-1.8 0-3.2 1.4-3.2 3.1s1.4 3.1 3.2 3.1 3.2-1.4 3.2-3.1V2z" />
  </svg>
);

const ICONS = { instagram: Instagram, facebook: Facebook, tiktok: TikTokIcon, linkedin: Linkedin, youtube: Youtube, twitter: Twitter };

export function ChannelIcon({ id, className = 'w-3 h-3', colored = true, style }) {
  const Icon = ICONS[id] || Globe;
  const meta = getChannelMeta(id);
  return <Icon className={className} style={{ ...(colored ? { color: meta.color } : {}), ...style }} aria-label={meta.label} />;
}

// Fila de iconos pequeños (casilla del calendario, matriz de control, listados)
export function ChannelIconsRow({ post, channels, className = 'w-3 h-3', colored = true, gap = 'gap-0.5', testId }) {
  const list = channels || getPostChannels(post);
  if (!list.length) return null;
  return (
    <span className={`inline-flex items-center ${gap}`} title={list.map((c) => getChannelMeta(c).label).join(' · ')} data-testid={testId}>
      {list.map((c) => <ChannelIcon key={c} id={c} className={className} colored={colored} />)}
    </span>
  );
}

// Selector múltiple tipo chips (popup de la publicación)
export function ChannelMultiSelect({ value = [], onChange, disabled = false, testPrefix = 'post-canal' }) {
  const toggle = (id) => {
    if (disabled) return;
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Canales" data-testid={`${testPrefix}-multiselect`}>
      {CONTENT_CHANNELS.map((c) => {
        const active = value.includes(c.id);
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => toggle(c.id)}
            disabled={disabled}
            aria-pressed={active}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
              active ? 'border-transparent text-white shadow-sm' : 'border-border bg-background text-muted-foreground hover:bg-muted'
            } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
            style={active ? { backgroundColor: c.color } : undefined}
            data-testid={`${testPrefix}-chip-${c.id}`}
          >
            <ChannelIcon id={c.id} className="w-3.5 h-3.5" colored={!active} />
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

// Etiquetas con nombre completo (detalle de publicación, portal del cliente)
export function ChannelLabels({ post, channels, className = '', emptyText = null, testId }) {
  const list = channels || getPostChannels(post);
  if (!list.length) return emptyText ? <span className={className}>{emptyText}</span> : null;
  return (
    <span className={`inline-flex flex-wrap items-center gap-1 ${className}`} data-testid={testId}>
      {list.map((c) => {
        const m = getChannelMeta(c);
        return (
          <span key={c} className="inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] font-medium" style={{ borderColor: `${m.color}55`, color: m.color, backgroundColor: `${m.color}12` }}>
            <ChannelIcon id={c} className="w-3 h-3" />
            {m.label}
          </span>
        );
      })}
    </span>
  );
}
